/**
 * Drag handles for the width of the chat column.
 *
 * The column is centered, so there is no panel edge to grab: the handles are an
 * overlay pinned to where the column's edges land, and they stay invisible
 * until a pointer comes near. Reading a conversation is the common case, and a
 * permanent rule down each side of it is chrome the reader never asked for.
 *
 * Both edges move the same measurement. Dragging one edge outward by a pixel
 * widens the column by two, because the other edge moves with it — the handle
 * following the pointer is the only behavior that does not feel broken.
 */
import { useCallback, useRef, useState } from 'react';
import { cn } from '@reflex/ui/lib/utils';
import { useElementWidth } from '@reflex/ui/hooks/useElementWidth';
import { usePointerSession } from '@reflex/ui/hooks/usePointerSession';
import {
  CHAT_COLUMN_DEFAULT_WIDTH_PX,
  CHAT_COLUMN_MAX_WIDTH_PX,
  CHAT_COLUMN_MIN_WIDTH_PX,
  CHAT_COLUMN_WIDTH_VAR,
  clampChatColumnWidth,
  useChatColumnWidth,
} from './chat-column.js';

/** One arrow key. Coarse enough to cross the range without holding the key down. */
const KEYBOARD_STEP_PX = 32;

const SIDES = ['left', 'right'] as const;
type Side = (typeof SIDES)[number];

/**
 * Where an edge sits: half the column out from the center, capped at the pane
 * the same way the column itself is (`w-full` beside its `max-w`). Expressed in
 * CSS so the handles track the width variable directly — a dragged edge then
 * costs no React render at all.
 */
function edgeOffset(side: Side): string {
  const half = `min(var(${CHAT_COLUMN_WIDTH_VAR}, 48rem), 100%) / 2`;
  return side === 'left' ? `calc(50% - ${half})` : `calc(50% + ${half})`;
}

interface DragState {
  side: Side;
  startX: number;
  startWidth: number;
}

export interface ChatColumnResizerProps {
  className?: string;
}

export function ChatColumnResizer({ className }: ChatColumnResizerProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  // The overlay is `inset-0` of the pane, so its width is the pane's.
  const paneWidth = useElementWidth(overlayRef);
  const [width, setWidth] = useChatColumnWidth(paneWidth);
  const [drag, setDrag] = useState<DragState | null>(null);
  const liveWidth = useRef(width);

  /**
   * Publish a width mid-drag without going through React or `localStorage`.
   *
   * The pane that hosts this overlay is the element that publishes the column
   * width, so writing the property there moves the transcript, the queued
   * messages, the composer and both handles in one paint. Committing per
   * pointer event instead would write to disk and re-render a transcript of
   * thousands of rows on every move; the commit happens once, on release.
   */
  const preview = useCallback((next: number) => {
    liveWidth.current = next;
    overlayRef.current?.parentElement?.style.setProperty(CHAT_COLUMN_WIDTH_VAR, `${next}px`);
  }, []);

  usePointerSession(
    drag !== null,
    'col-resize',
    useCallback(
      (event: PointerEvent) => {
        if (!drag) return;
        const travel = (event.clientX - drag.startX) * (drag.side === 'right' ? 2 : -2);
        preview(clampChatColumnWidth(drag.startWidth + travel, paneWidth));
      },
      [drag, paneWidth, preview],
    ),
    useCallback(() => {
      setDrag(null);
      setWidth(liveWidth.current);
    }, [setWidth]),
  );

  const nudge = (side: Side, event: React.KeyboardEvent<HTMLDivElement>) => {
    // An arrow moves the handle, so which key widens depends on the edge.
    const outward = side === 'right' ? 'ArrowRight' : 'ArrowLeft';
    const inward = side === 'right' ? 'ArrowLeft' : 'ArrowRight';
    if (event.key === outward) setWidth(width + KEYBOARD_STEP_PX);
    else if (event.key === inward) setWidth(width - KEYBOARD_STEP_PX);
    else if (event.key === 'Home') setWidth(CHAT_COLUMN_DEFAULT_WIDTH_PX);
    else return;
    event.preventDefault();
  };

  return (
    <div
      ref={overlayRef}
      // Only the handles take pointer events: a wheel or a drag anywhere else
      // over the gutter still belongs to the transcript underneath. Hidden on
      // mobile, where the column is the pane and there is nothing to give.
      className={cn(
        'group/resizer pointer-events-none absolute inset-0 z-20 hidden md:block',
        className,
      )}
      data-testid="chat-column-resizer"
      // Drives both rules from one place: the pair is one control, so reaching
      // for either edge shows both. Only the handles take pointer events, so
      // this hover state is a handle's, not the whole pane's.
      data-dragging={drag ? 'true' : undefined}
    >
      {SIDES.map((side) => (
        <div
          key={side}
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize chat column, ${side} edge`}
          aria-valuenow={width}
          aria-valuemin={CHAT_COLUMN_MIN_WIDTH_PX}
          aria-valuemax={CHAT_COLUMN_MAX_WIDTH_PX}
          tabIndex={0}
          data-testid={`chat-column-resize-${side}`}
          data-dragging={drag?.side === side ? 'true' : undefined}
          className="pointer-events-auto absolute inset-y-0 flex w-4 -translate-x-1/2 cursor-col-resize justify-center focus-visible:outline-none"
          style={{ left: edgeOffset(side) }}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            // Keeps the press from starting a text selection under the overlay.
            event.preventDefault();
            liveWidth.current = width;
            setDrag({ side, startX: event.clientX, startWidth: width });
          }}
          onDoubleClick={() => setWidth(CHAT_COLUMN_DEFAULT_WIDTH_PX)}
          onKeyDown={(event) => nudge(side, event)}
        >
          <span
            aria-hidden
            // A hairline, and dim: it reads against the transcript's own
            // borders without competing with them.
            className={cn(
              'h-full w-px bg-muted-foreground/40 opacity-0 transition-opacity duration-150',
              'group-hover/resizer:opacity-100',
              'group-focus-within/resizer:bg-ring group-focus-within/resizer:opacity-100',
              'group-data-[dragging=true]/resizer:bg-ring group-data-[dragging=true]/resizer:opacity-100',
            )}
          />
        </div>
      ))}
    </div>
  );
}
